import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { ExecutionRunsTable } from '@/features/executionRuns/components/ExecutionRunsTable';
import { queryExecutionRuns } from '@/features/executionRuns/data/executionRuns';
import { useExecutionRunsPageViewModel } from '@/features/executionRuns/hooks/useExecutionRunsPageViewModel';
import { ExecutionRunsPageCharts } from '@/features/executionRuns/pages/ExecutionRunsPageCharts';
import { ExecutionRunsPageDialogs } from '@/features/executionRuns/pages/ExecutionRunsPageDialogs';
import {
  resolveExecutionRunsRouteNotice,
  resolveForceOpenTutorial,
  resolveOnboardingJustCompleted,
  type ExecutionRunsPageLocationState,
} from '@/features/executionRuns/pages/executionRunsPageLocationState';
import { buildExecutionRunsPageTableQuery } from '@/features/executionRuns/pages/executionRunsPageTableQuery';
import { executionRunsPageTabs } from '@/features/executionRuns/pages/executionRunsPageTabs';
import {
  useResultsFilesTagOptions,
  useUploadsTagOptions,
} from '@/features/executionRuns/pages/executionRunsPageTagOptions';
import {
  executionRunsTableDefinition,
  type ExecutionRunsSortKey,
} from '@/features/executionRuns/pages/executionRunsTableDefinition';
import { useExecutionRunsTableToolbarState } from '@/features/executionRuns/pages/executionRunsTableToolbarState';
import {
  resolveExecutionRunsToolbarDates,
  resolveExecutionRunsToolbarQuickFilter,
} from '@/features/executionRuns/pages/executionRunsToolbar';
import { ExecutionRunsUploadAction } from '@/features/executionRuns/pages/ExecutionRunsUploadAction';
import { buildExecutionRunsUploadProjectOptions } from '@/features/executionRuns/pages/executionRunsUploadProjectOptions';
import {
  resultsFilesTableDefinition,
  type ResultsFilesSortKey,
} from '@/features/executionRuns/pages/resultsFilesTableDefinition';
import {
  uploadsTableDefinition,
  type UploadsSortKey,
} from '@/features/executionRuns/pages/uploadsTableDefinition';
import { useExecutionRunsColumnVisibilityModels } from '@/features/executionRuns/pages/useExecutionRunsColumnVisibilityModels';
import { usePendingPostOnboardingState } from '@/features/executionRuns/pages/usePendingPostOnboardingState';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { usePostOnboardingTutorialState } from '@/features/postOnboardingTutorial';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { MountedTableTabPanel } from '@/features/shared/components/tableSystem/MountedTableTabPanel';
import { NativeTablePanelTabs } from '@/features/shared/components/tableSystem/NativeTablePanelTabs';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { useMountedTableTabs } from '@/features/shared/components/tableSystem/useMountedTableTabs';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { ResultsFilesTable } from '@/features/uploads/components/ResultsFilesTable';
import { UploadsTable } from '@/features/uploads/components/UploadsTable';
import { queryResultsFiles, queryUploads } from '@/features/uploads/data';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getErrorCopy } from '@/shared/copy/errors';
import { useOrganisationEntitlements } from '@/shared/hooks';
import { resolvePlanStatus } from '@/shared/plans/planStatus';
import { StatusBanner } from '@/ui/composites';
import { buildEqFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import { Box, Text } from '@/ui/primitives';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type {
  ExecutionRunDto,
  ResultsFileDto,
  ResultsFileStatus,
  SuiteRunStatus,
  UploadDto,
  UploadStatus,
} from '@flowqa/shared';
import type { ReactElement } from 'react';

const ExecutionRunsPageContent = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const showForcedLoadingState = searchParams.get('loadState') === 'loading';
  const locationState = location.state as ExecutionRunsPageLocationState;
  const routeNotice = resolveExecutionRunsRouteNotice(locationState);
  const forceOpenTutorial = resolveForceOpenTutorial(searchParams.get('tutorial'));
  const [onboardingDisclaimerOpen, setOnboardingDisclaimerOpen] = useState(false);
  const [visibleRouteNotice, setVisibleRouteNotice] = useState<string | null>(routeNotice);
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const pendingPostOnboardingState = usePendingPostOnboardingState(session?.userId);
  const onboardingJustCompleted = resolveOnboardingJustCompleted(
    locationState,
    pendingPostOnboardingState,
  );
  const {
    range,
    setRange,
    searchValue,
    setSearchValue,
    activeTab,
    setActiveTab,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    projectFilterOptions,
    showProjectFilter,
    statusOptions,
    onStatusesChange,
    onTagsChange,
    onStatusToggle,
    onProjectsChange,
    executionRunMetricCards,
    executionRunMetricsLoading,
    selectedProjectId,
    tagOptions,
    chart,
    datePicker,
    filters,
    uploadDialog,
  } = useExecutionRunsPageViewModel();
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const entitlements = entitlementsQuery.data ?? null;
  const {
    executionRunsColumnVisibilityModel,
    handleExecutionRunsColumnVisibilityModelChange,
    handleResultsFilesColumnVisibilityModelChange,
    handleUploadsColumnVisibilityModelChange,
    resultsFilesColumnVisibilityModel,
    uploadsColumnVisibilityModel,
  } = useExecutionRunsColumnVisibilityModels();
  const [executionRunsSortKey, setExecutionRunsSortKey] = useState<ExecutionRunsSortKey>(
    executionRunsTableDefinition.defaultSortKey,
  );
  const [executionRunsSortDirection, setExecutionRunsSortDirection] = useState<'asc' | 'desc'>(
    executionRunsTableDefinition.defaultSortDirection,
  );
  const [resultsFilesSortKey, setResultsFilesSortKey] = useState<ResultsFilesSortKey>(
    resultsFilesTableDefinition.defaultSortKey,
  );
  const [resultsFilesSortDirection, setResultsFilesSortDirection] = useState<'asc' | 'desc'>(
    resultsFilesTableDefinition.defaultSortDirection,
  );
  const [uploadsSortKey, setUploadsSortKey] = useState<UploadsSortKey>(
    uploadsTableDefinition.defaultSortKey,
  );
  const [uploadsSortDirection, setUploadsSortDirection] = useState<'asc' | 'desc'>(
    uploadsTableDefinition.defaultSortDirection,
  );
  const [executionRunsMeta, setExecutionRunsMeta] =
    useState<TableQueryMeta<ExecutionRunDto> | null>(null);
  const [resultsFilesMeta, setResultsFilesMeta] = useState<TableQueryMeta<ResultsFileDto> | null>(
    null,
  );
  const [uploadsMeta, setUploadsMeta] = useState<TableQueryMeta<UploadDto> | null>(null);
  const mountedTableTabs = useMountedTableTabs(activeTab);
  const resultsFilesToolbarState = useExecutionRunsTableToolbarState<ResultsFileStatus>({
    dateField: 'createdAt',
    noRowsBaseText: getEmptyStateMessage('data.noResultsFound'),
    controlledDateState: {
      range,
      setRange,
      startDay: datePicker.startDay,
      setStartDay: datePicker.onStartDayChange,
      endDay: datePicker.endDay,
      setEndDay: datePicker.onEndDayChange,
    },
  });
  const uploadsToolbarState = useExecutionRunsTableToolbarState<UploadStatus>({
    dateField: 'createdAt',
    noRowsBaseText: getEmptyStateMessage('entity.noneFound', { entity: 'upload' }),
    controlledDateState: {
      range,
      setRange,
      startDay: datePicker.startDay,
      setStartDay: datePicker.onStartDayChange,
      endDay: datePicker.endDay,
      setEndDay: datePicker.onEndDayChange,
    },
  });
  const executionRunsAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const { availableTags: resultsFilesAvailableTags } = useResultsFilesTagOptions({
    orgId: selectedOrganisationId ?? null,
    projectId: selectedProjectId,
    filters: resultsFilesToolbarState.fixedFilters,
    enabled: activeTab === 'results-files',
  });
  const uploadsFixedFilters = useMemo(
    () => mergeFilters(uploadsToolbarState.fixedFilters, buildEqFilter('purpose', 'results')),
    [uploadsToolbarState.fixedFilters],
  );
  const { availableTags: uploadsAvailableTags } = useUploadsTagOptions({
    orgId: selectedOrganisationId ?? null,
    projectId: selectedProjectId,
    filters: uploadsFixedFilters,
    enabled: activeTab === 'uploads',
  });
  const executionRunsQueryKey = useMemo(
    () => ['execution-runs-table-toolbar', executionRunsSortKey, executionRunsSortDirection],
    [executionRunsSortDirection, executionRunsSortKey],
  );
  const resultsFilesQueryKey = useMemo(
    () => ['results-files-table-toolbar', resultsFilesSortKey, resultsFilesSortDirection],
    [resultsFilesSortDirection, resultsFilesSortKey],
  );
  const uploadsQueryKey = useMemo(
    () => ['uploads-table-toolbar', uploadsSortKey, uploadsSortDirection],
    [uploadsSortDirection, uploadsSortKey],
  );

  const executionRunsQueryFn = useMemo(
    () =>
      buildExecutionRunsPageTableQuery({
        query: queryExecutionRuns,
        extras: {
          includeSummary: true,
          includeSummaryDetails: false,
        },
        sortFieldMap: executionRunsTableDefinition.sortFieldMap,
        sortKey: executionRunsSortKey,
        sortDirection: executionRunsSortDirection,
      }),
    [executionRunsSortDirection, executionRunsSortKey],
  );
  const resultsFilesQueryFn = useMemo(
    () =>
      buildExecutionRunsPageTableQuery({
        query: queryResultsFiles,
        extras: {
          includeSummary: true,
          includeSummaryDetails: true,
          include: { project: false, createdBy: true, createdByEmail: true },
        },
        sortFieldMap: resultsFilesTableDefinition.sortFieldMap,
        sortKey: resultsFilesSortKey,
        sortDirection: resultsFilesSortDirection,
      }),
    [resultsFilesSortDirection, resultsFilesSortKey],
  );
  const uploadsQueryFn = useMemo(
    () =>
      buildExecutionRunsPageTableQuery({
        query: queryUploads,
        extras: {
          includeSummary: true,
          includeSummaryDetails: false,
        },
        sortFieldMap: uploadsTableDefinition.sortFieldMap,
        sortKey: uploadsSortKey,
        sortDirection: uploadsSortDirection,
      }),
    [uploadsSortDirection, uploadsSortKey],
  );
  const planStatus = useMemo(
    () =>
      resolvePlanStatus({
        entitlements,
        projects: uploadDialog.projectOptions.map((project) => ({
          id: project.id,
          name: project.name,
          slug: project.slug,
          badgeChars: project.badgeChars,
          badgeColor: project.badgeColor,
          badgeEnabled: project.badgeEnabled,
          createdAt: project.createdAt,
        })),
        selectedProjectId,
      }),
    [entitlements, selectedProjectId, uploadDialog.projectOptions],
  );
  const tutorial = usePostOnboardingTutorialState({
    userId: session?.userId ?? null,
    organisationId: selectedOrganisationId ?? null,
    projectId: selectedProjectId ?? null,
    onboardingCompleted: session?.onboardingCompleted === true,
    onboardingJustCompleted,
    forceOpen: forceOpenTutorial,
  });
  const uploadBlockedReason = planStatus.uploadBlockedReason;
  const uploadProjectOptions = useMemo(
    () =>
      buildExecutionRunsUploadProjectOptions({
        planProjectOptions: planStatus.projectOptions,
        projectOptions: uploadDialog.projectOptions,
      }),
    [planStatus.projectOptions, uploadDialog.projectOptions],
  );
  const handleUploadConfirm = (): void => {
    const projectId = selectedProjectId ?? uploadDialog.uploadProjectId;
    if (!projectId) {
      uploadDialog.setUploadError(getErrorCopy('upload.selectProject'));
      return;
    }
    const selectedProject = uploadProjectOptions.find((project) => project.id === projectId);
    if (selectedProject?.locked) {
      uploadDialog.setUploadError(
        selectedProject.lockReason ?? 'Uploads are locked for this project on the current plan.',
      );
      return;
    }
    if (uploadDialog.selectedUploadFiles.length === 0) {
      uploadDialog.setUploadError(getErrorCopy('upload.selectSupportedFile'));
      return;
    }
    uploadDialog.setUploadError(null);
    const filesToUpload = uploadDialog.selectedUploadFiles;
    const tagsToUpload = uploadDialog.uploadTagValues;
    const tagColorsToUpload = uploadDialog.uploadTagColors;
    uploadDialog.closeUploadDialog();
    uploadDialog.startUpload(projectId, filesToUpload, tagsToUpload, tagColorsToUpload);
  };
  const executionRunsToolbarDates = useMemo(
    () =>
      resolveExecutionRunsToolbarDates({
        hasCustomDates: datePicker.hasCustomDates,
        startDay: datePicker.startDay,
        endDay: datePicker.endDay,
      }),
    [datePicker.endDay, datePicker.hasCustomDates, datePicker.startDay],
  );
  const executionRunsNoRowsText = useMemo(
    () =>
      formatRangeWindowNoDataText(
        getEmptyStateMessage('entity.noneFound', { entity: 'executionRun' }),
        range,
        datePicker.hasCustomDates,
      ),
    [datePicker.hasCustomDates, range],
  );

  useEffect(() => {
    setVisibleRouteNotice(routeNotice);
    if (!routeNotice) return;
    const { notice: _notice, ...remainingState } = locationState ?? {};
    const nextState = Object.keys(remainingState).length > 0 ? remainingState : null;
    void navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: nextState,
    });
  }, [location.pathname, location.search, locationState, navigate, routeNotice]);

  useEffect(() => {
    if (
      locationState?.postOnboardingDisclaimer !== true &&
      pendingPostOnboardingState?.showDisclaimer !== true
    )
      return;
    setOnboardingDisclaimerOpen(true);
  }, [locationState?.postOnboardingDisclaimer, pendingPostOnboardingState?.showDisclaimer]);

  const executionRunsUnifiedToolbarState = useMemo(
    () => ({
      search: searchValue,
      setSearch: setSearchValue,
      selectedStatuses,
      setSelectedStatuses: onStatusesChange,
      selectedTags,
      setSelectedTags: onTagsChange,
      selectedProjects,
      setSelectedProjects: onProjectsChange,
      projectOptions: showProjectFilter ? projectFilterOptions : undefined,
      noRowsText: executionRunsNoRowsText,
      dateQuickFilter: resolveExecutionRunsToolbarQuickFilter(range),
      onDateQuickFilterChange: setRange,
      fromDate: executionRunsToolbarDates.fromDate,
      toDate: executionRunsToolbarDates.toDate,
      onFromDateChange: (next: string | null) => {
        datePicker.onStartDayChange(next);
      },
      onToDateChange: (next: string | null) => {
        datePicker.onEndDayChange(next);
      },
    }),
    [
      datePicker,
      executionRunsNoRowsText,
      executionRunsToolbarDates.fromDate,
      executionRunsToolbarDates.toDate,
      onStatusesChange,
      onTagsChange,
      range,
      searchValue,
      selectedStatuses,
      selectedProjects,
      selectedTags,
      setRange,
      setSearchValue,
      showProjectFilter,
      projectFilterOptions,
    ],
  );
  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Execution Runs
        </Text>
      }
      titleActions={
        <ExecutionRunsUploadAction
          disabledReason={uploadBlockedReason}
          onClick={uploadDialog.openUploadDialog}
        />
      }
      charts={
        <ExecutionRunsPageCharts
          availableTags={executionRunsAvailableTags}
          groupErroredWithFailed={groupErroredWithFailed}
          loading={showForcedLoadingState || authLoading || executionRunMetricsLoading}
          metricCards={executionRunMetricCards}
          onStatusToggle={onStatusToggle}
          projectId={selectedProjectId}
          selectedStatusIds={selectedStatuses}
          showPoints={chart.showPoints}
          statusFilterLabel={executionRunsTableDefinition.statusFilterLabel}
          statusOptions={statusOptions}
          suppressAnimation={chart.suppressAnimation}
          toolbarState={executionRunsUnifiedToolbarState}
        />
      }
      content={
        <Panel
          padding="md"
          gap="none"
          contentGap="md"
          headerGap="none"
          headerSlot={
            <Box
              sx={{
                backgroundColor: 'background.paper',
                borderBottom: 'none',
                minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
                position: 'relative',
                '&::after': buildTableDividerPseudoSx('bottom'),
              }}
            >
              <Stack
                direction="row"
                align="flex-end"
                justify="space-between"
                gap="sm"
                sx={{
                  minWidth: 0,
                  minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
                  height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
                }}
              >
                <Box sx={{ minWidth: 0, flex: '1 1 auto', position: 'relative', zIndex: 2 }}>
                  <NativeTablePanelTabs
                    value={activeTab}
                    onChange={(next) => {
                      window.requestAnimationFrame(() => {
                        setActiveTab(next);
                      });
                    }}
                    ariaLabel="Execution runs page tabs"
                    tabs={executionRunsPageTabs}
                  />
                </Box>
                <Box sx={{ minWidth: 0, flex: '0 1 auto', position: 'relative', zIndex: 1 }}>
                  {activeTab === 'execution-runs' ? (
                    <TableToolbar<ExecutionRunsSortKey, SuiteRunStatus>
                      title={undefined}
                      searchAriaLabel={executionRunsTableDefinition.searchAriaLabel}
                      searchValue={searchValue}
                      onDebouncedSearchChange={setSearchValue}
                      columnVisibilityModel={executionRunsColumnVisibilityModel}
                      defaultColumnVisibilityModel={
                        executionRunsTableDefinition.defaultColumnVisibilityModel
                      }
                      columnOptions={executionRunsTableDefinition.columnOptions}
                      onColumnVisibilityModelChange={handleExecutionRunsColumnVisibilityModelChange}
                      sortKey={executionRunsSortKey}
                      sortDirection={executionRunsSortDirection}
                      defaultSortKey={executionRunsTableDefinition.defaultSortKey}
                      defaultSortDirection={executionRunsTableDefinition.defaultSortDirection}
                      sortOptions={executionRunsTableDefinition.sortOptions}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setExecutionRunsSortKey(nextSortKey);
                        setExecutionRunsSortDirection(nextSortDirection);
                      }}
                      filterStatuses={selectedStatuses}
                      statusOptions={statusOptions}
                      onFilterStatusesChange={(next) => {
                        onStatusesChange(next as SuiteRunStatus[]);
                      }}
                      filterTags={selectedTags}
                      onFilterTagsChange={onTagsChange}
                      dateQuickFilter={resolveExecutionRunsToolbarQuickFilter(range)}
                      onDateQuickFilterChange={setRange}
                      fromDate={executionRunsToolbarDates.fromDate}
                      toDate={executionRunsToolbarDates.toDate}
                      onFromDateChange={(next) => {
                        datePicker.onStartDayChange(next ? next.format('YYYY-MM-DD') : null);
                      }}
                      onToDateChange={(next) => {
                        datePicker.onEndDayChange(next ? next.format('YYYY-MM-DD') : null);
                      }}
                      availableTags={executionRunsAvailableTags}
                      statusFilterLabel={executionRunsTableDefinition.statusFilterLabel}
                      summaryCount={executionRunsMeta?.total ?? null}
                      summarySingularLabel="execution run"
                      summaryPluralLabel="execution runs"
                      hideDateRangeLabel
                      showControlsWithoutSummary
                    />
                  ) : activeTab === 'results-files' ? (
                    <TableToolbar<
                      ResultsFilesSortKey,
                      (typeof resultsFilesToolbarState.selectedStatuses)[number]
                    >
                      title={undefined}
                      searchAriaLabel={resultsFilesTableDefinition.searchAriaLabel}
                      searchValue={resultsFilesToolbarState.search}
                      onDebouncedSearchChange={resultsFilesToolbarState.setSearch}
                      columnVisibilityModel={resultsFilesColumnVisibilityModel}
                      defaultColumnVisibilityModel={
                        resultsFilesTableDefinition.defaultColumnVisibilityModel
                      }
                      columnOptions={resultsFilesTableDefinition.columnOptions}
                      onColumnVisibilityModelChange={handleResultsFilesColumnVisibilityModelChange}
                      sortKey={resultsFilesSortKey}
                      sortDirection={resultsFilesSortDirection}
                      defaultSortKey={resultsFilesTableDefinition.defaultSortKey}
                      defaultSortDirection={resultsFilesTableDefinition.defaultSortDirection}
                      sortOptions={resultsFilesTableDefinition.sortOptions}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setResultsFilesSortKey(nextSortKey);
                        setResultsFilesSortDirection(nextSortDirection);
                      }}
                      filterStatuses={resultsFilesToolbarState.selectedStatuses}
                      statusOptions={resultsFilesTableDefinition.statusOptions}
                      onFilterStatusesChange={(next) => {
                        resultsFilesToolbarState.setSelectedStatuses(
                          next as Array<(typeof resultsFilesToolbarState.selectedStatuses)[number]>,
                        );
                      }}
                      filterTags={resultsFilesToolbarState.selectedTags}
                      onFilterTagsChange={resultsFilesToolbarState.setSelectedTags}
                      dateQuickFilter={resultsFilesToolbarState.dateQuickFilter}
                      onDateQuickFilterChange={resultsFilesToolbarState.onDateQuickFilterChange}
                      fromDate={resultsFilesToolbarState.fromDate}
                      toDate={resultsFilesToolbarState.toDate}
                      onFromDateChange={(next) => {
                        resultsFilesToolbarState.onFromDateChange(
                          next ? next.format('YYYY-MM-DD') : null,
                        );
                      }}
                      onToDateChange={(next) => {
                        resultsFilesToolbarState.onToDateChange(
                          next ? next.format('YYYY-MM-DD') : null,
                        );
                      }}
                      availableTags={resultsFilesAvailableTags}
                      statusFilterLabel={resultsFilesTableDefinition.statusFilterLabel}
                      summaryCount={resultsFilesMeta?.total ?? null}
                      summarySingularLabel="results file"
                      summaryPluralLabel="results files"
                      hideDateRangeLabel
                      showControlsWithoutSummary
                    />
                  ) : (
                    <TableToolbar<
                      UploadsSortKey,
                      (typeof uploadsToolbarState.selectedStatuses)[number]
                    >
                      title={undefined}
                      searchAriaLabel={uploadsTableDefinition.searchAriaLabel}
                      searchValue={uploadsToolbarState.search}
                      onDebouncedSearchChange={uploadsToolbarState.setSearch}
                      columnVisibilityModel={uploadsColumnVisibilityModel}
                      defaultColumnVisibilityModel={
                        uploadsTableDefinition.defaultColumnVisibilityModel
                      }
                      columnOptions={uploadsTableDefinition.columnOptions}
                      onColumnVisibilityModelChange={handleUploadsColumnVisibilityModelChange}
                      sortKey={uploadsSortKey}
                      sortDirection={uploadsSortDirection}
                      defaultSortKey={uploadsTableDefinition.defaultSortKey}
                      defaultSortDirection={uploadsTableDefinition.defaultSortDirection}
                      sortOptions={uploadsTableDefinition.sortOptions}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setUploadsSortKey(nextSortKey);
                        setUploadsSortDirection(nextSortDirection);
                      }}
                      filterStatuses={uploadsToolbarState.selectedStatuses}
                      statusOptions={uploadsTableDefinition.statusOptions}
                      onFilterStatusesChange={(next) => {
                        uploadsToolbarState.setSelectedStatuses(
                          next as Array<(typeof uploadsToolbarState.selectedStatuses)[number]>,
                        );
                      }}
                      filterTags={uploadsToolbarState.selectedTags}
                      onFilterTagsChange={uploadsToolbarState.setSelectedTags}
                      dateQuickFilter={uploadsToolbarState.dateQuickFilter}
                      onDateQuickFilterChange={uploadsToolbarState.onDateQuickFilterChange}
                      fromDate={uploadsToolbarState.fromDate}
                      toDate={uploadsToolbarState.toDate}
                      onFromDateChange={(next) => {
                        uploadsToolbarState.onFromDateChange(
                          next ? next.format('YYYY-MM-DD') : null,
                        );
                      }}
                      onToDateChange={(next) => {
                        uploadsToolbarState.onToDateChange(next ? next.format('YYYY-MM-DD') : null);
                      }}
                      availableTags={uploadsAvailableTags}
                      statusFilterLabel={uploadsTableDefinition.statusFilterLabel}
                      summaryCount={uploadsMeta?.total ?? null}
                      summarySingularLabel="upload"
                      summaryPluralLabel="uploads"
                      hideDateRangeLabel
                      showControlsWithoutSummary
                    />
                  )}
                </Box>
              </Stack>
            </Box>
          }
          sx={tablePanelSurfaceSx}
        >
          <Stack gap="md">
            {visibleRouteNotice ? (
              <StatusBanner
                tone="blue"
                message={visibleRouteNotice}
                onClose={() => {
                  setVisibleRouteNotice(null);
                }}
              />
            ) : null}
            {mountedTableTabs.has('execution-runs') ? (
              <MountedTableTabPanel active={activeTab === 'execution-runs'}>
                <ExecutionRunsTable
                  persistKey="execution-runs-table-v5"
                  fixedFilters={filters.executionRuns}
                  search={searchValue}
                  tagOptionsOverride={tagOptions}
                  queryKey={executionRunsQueryKey}
                  queryFn={executionRunsQueryFn}
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialPageSize={5}
                  disableColumnSorting
                  disableColumnMenu
                  preservePreviousData
                  showSkeletonOnFetch={false}
                  showPaginationSkeletonWhenRowsVisible={false}
                  dynamicRowHeight={false}
                  active={activeTab === 'execution-runs'}
                  noRowsText={executionRunsNoRowsText}
                  onQueryMeta={setExecutionRunsMeta}
                  columnVisibilityModel={executionRunsColumnVisibilityModel}
                  onColumnVisibilityModelChange={handleExecutionRunsColumnVisibilityModelChange}
                />
              </MountedTableTabPanel>
            ) : null}
            {mountedTableTabs.has('results-files') ? (
              <MountedTableTabPanel active={activeTab === 'results-files'}>
                <ResultsFilesTable
                  persistKey="results-files-table"
                  fixedFilters={resultsFilesToolbarState.fixedFilters}
                  search={resultsFilesToolbarState.search}
                  tagOptionsOverride={tagOptions}
                  queryKey={resultsFilesQueryKey}
                  queryFn={resultsFilesQueryFn}
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialPageSize={5}
                  disableColumnSorting
                  disableColumnMenu
                  preservePreviousData
                  showSkeletonOnFetch={false}
                  showPaginationSkeletonWhenRowsVisible={false}
                  dynamicRowHeight={false}
                  active={activeTab === 'results-files'}
                  noRowsText={resultsFilesToolbarState.noRowsText}
                  onQueryMeta={setResultsFilesMeta}
                  columnVisibilityModel={resultsFilesColumnVisibilityModel}
                  onColumnVisibilityModelChange={handleResultsFilesColumnVisibilityModelChange}
                />
              </MountedTableTabPanel>
            ) : null}
            {mountedTableTabs.has('uploads') ? (
              <MountedTableTabPanel active={activeTab === 'uploads'}>
                <UploadsTable
                  persistKey="uploads-table"
                  fixedFilters={uploadsFixedFilters}
                  search={uploadsToolbarState.search}
                  tagOptionsOverride={tagOptions}
                  queryKey={uploadsQueryKey}
                  queryFn={uploadsQueryFn}
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialPageSize={5}
                  disableColumnSorting
                  disableColumnMenu
                  preservePreviousData
                  showSkeletonOnFetch={false}
                  showPaginationSkeletonWhenRowsVisible={false}
                  dynamicRowHeight={false}
                  active={activeTab === 'uploads'}
                  noRowsText={uploadsToolbarState.noRowsText}
                  onQueryMeta={setUploadsMeta}
                  columnVisibilityModel={uploadsColumnVisibilityModel}
                  onColumnVisibilityModelChange={handleUploadsColumnVisibilityModelChange}
                />
              </MountedTableTabPanel>
            ) : null}
          </Stack>
        </Panel>
      }
      footer={
        <ExecutionRunsPageDialogs
          disclaimerDialog={{
            open: onboardingDisclaimerOpen,
            onClose: () => {
              setOnboardingDisclaimerOpen(false);
            },
          }}
          handleUploadConfirm={handleUploadConfirm}
          navigate={navigate}
          selectedOrganisationId={selectedOrganisationId}
          selectedProjectId={selectedProjectId}
          tutorial={tutorial}
          uploadDialog={uploadDialog}
          uploadBlockedReason={uploadBlockedReason}
          uploadProjectOptions={uploadProjectOptions}
        />
      }
    />
  );
};

export { ExecutionRunsPageContent };
